import { Mic, Scroll } from 'lucide-react';
import { DiscoveryCard } from './discovery-card';
import { SourceLinks } from '@/components/ui/source-links';

interface OralTraditionCardProps {
  oralTradition: {
    evidenceOfOrality?: string;
    performanceContext?: string;
    sources?: Source[];
  };
  isExpanded: boolean;
  onToggleExpansion: () => void;
  thumbnailUrl?: string;
}

/**
 * Shared component for displaying oral tradition information
 * Used in both the literary context page and the capability modal
 */
export function OralTraditionCard({ oralTradition, isExpanded, onToggleExpansion, thumbnailUrl }: OralTraditionCardProps) {
  if (!oralTradition || (!oralTradition.evidenceOfOrality && !oralTradition.performanceContext)) return null;

  return (
    <DiscoveryCard
      isExpanded={isExpanded}
      onToggle={onToggleExpansion}
      icon={Mic}
      thumbnailUrl={thumbnailUrl}
      header={
        <div className="flex flex-col gap-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary/50">Structural Module</div>
          <h3 className="text-xl font-serif font-medium text-foreground/90 leading-tight">Oral Tradition & Performance</h3>
        </div>
      }
    >
      <div className="pt-4 space-y-6">
        {oralTradition.evidenceOfOrality && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/40" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary/40">Evidence of Orality</span>
            </div>
            <p className="text-sm text-foreground/80 font-light leading-relaxed pl-3.5 border-l border-primary/10">
              {oralTradition.evidenceOfOrality}
            </p>
          </div>
        )}

        {oralTradition.performanceContext && (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <div className="w-1.5 h-1.5 rounded-full bg-primary/40" />
              <span className="text-[10px] font-bold uppercase tracking-widest text-primary/40">Performance Context</span>
            </div>
            <p className="text-sm text-foreground/80 font-light leading-relaxed italic pl-3.5 border-l border-primary/10">
              {oralTradition.performanceContext}
            </p>
          </div>
        )}

        {oralTradition.sources && oralTradition.sources.length > 0 && (
          <div className="pt-4 mt-6 border-t border-border/5">
            <div className="flex items-center gap-2 mb-3">
              <Scroll className="h-3 w-3 text-muted-foreground/40" />
              <span className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground/40">Technical Sources</span>
            </div>
            <SourceLinks sources={oralTradition.sources} />
          </div>
        )}
      </div>
    </DiscoveryCard>
  );
}

